<template>
  <transition name="slide-down">
    <div
      v-if="show"
      class="notification-toast"
      :class="getToastClass(notification.type)"
      @click="handleClick"
    >
      <!-- Close button -->
      <button class="toast-close" @click.stop="handleClose">
        <v-icon small color="white">mdi-close</v-icon>
      </button>

      <!-- Icon -->
      <div class="toast-icon">
        <v-icon :color="getIconColor(notification.type)">
          {{ getIcon(notification.type) }}
        </v-icon>
      </div>

      <!-- Content -->
      <div class="toast-content">
        <div class="toast-title">{{ notification.subject }}</div>
        <div class="toast-message" v-html="getPreviewText(notification.text)"></div>
        <div class="toast-time">{{ formatTime(notification.created) }}</div>
      </div>

      <!-- Progress bar -->
      <div class="toast-progress" :style="{ width: progressWidth + '%' }"></div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'NotificationToast',
  props: {
    notification: {
      type: Object,
      required: true
    },
    duration: {
      type: Number,
      default: 8000 // 8 sekundi
    }
  },
  data () {
    return {
      show: false,
      progressWidth: 100,
      progressInterval: null,
      autoCloseTimeout: null
    }
  },
  mounted () {
    // Prikaži toast sa animacijom
    this.$nextTick(() => {
      this.show = true
    })

    // Pokreni progress bar
    this.startProgress()

    // Auto-close nakon duration
    this.autoCloseTimeout = setTimeout(() => {
      this.handleClose()
    }, this.duration)
  },
  beforeDestroy () {
    this.clearTimers()
  },
  methods: {
    getIcon (type) {
      switch (type) {
        case 'CONFIRMATION': return 'mdi-alert-circle'
        case 'IMPORTANT': return 'mdi-information'
        case 'WARNING': return 'mdi-alert'
        case 'NOTIFICATION': return 'mdi-bell'
        default: return 'mdi-bell'
      }
    },

    getIconColor (type) {
      return 'white'
    },

    getToastClass (type) {
      switch (type) {
        case 'CONFIRMATION': return 'toast-warning'
        case 'IMPORTANT': return 'toast-important'
        case 'WARNING': return 'toast-warning'
        case 'NOTIFICATION': return 'toast-info'
        default: return 'toast-info'
      }
    },

    getPreviewText (html) {
      if (!html) return ''

      // Ukloni HTML tagove
      const div = document.createElement('div')
      div.innerHTML = html
      const text = div.textContent || div.innerText || ''

      // Skrati na max 80 karaktera
      return text.length > 80 ? text.substring(0, 80) + '...' : text
    },

    formatTime (timestamp) {
      if (!timestamp) return ''

      let notifTime = typeof timestamp === 'number' ? timestamp : (timestamp.toMillis ? timestamp.toMillis() : timestamp)

      // Ako je epoch u sekundama, pretvori u milisekunde
      if (notifTime < 10000000000) {
        notifTime = notifTime * 1000
      }

      const now = Date.now()
      const diff = now - notifTime

      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)

      if (minutes < 1) return 'Upravo sada'
      if (minutes < 60) return `Prije ${minutes} min`
      if (hours < 24) return `Prije ${hours}h`

      const date = new Date(notifTime)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const hours24 = String(date.getHours()).padStart(2, '0')
      const mins = String(date.getMinutes()).padStart(2, '0')

      return `${day}.${month}. ${hours24}:${mins}`
    },

    startProgress () {
      const interval = 50 // Update svakih 50ms
      const decrement = (100 / this.duration) * interval

      this.progressInterval = setInterval(() => {
        this.progressWidth -= decrement
        if (this.progressWidth <= 0) {
          this.progressWidth = 0
          clearInterval(this.progressInterval)
        }
      }, interval)
    },

    clearTimers () {
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
      }
      if (this.autoCloseTimeout) {
        clearTimeout(this.autoCloseTimeout)
      }
    },

    handleClick () {
      this.clearTimers()
      this.$emit('click', this.notification)
    },

    handleClose () {
      this.clearTimers()
      this.show = false

      // Čekaj da se animacija završi prije emitovanja close
      setTimeout(() => {
        this.$emit('close', this.notification)
      }, 300)
    }
  }
}
</script>

<style scoped>
.notification-toast {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 360px;
  max-width: 420px;
  padding: 16px 20px 16px 16px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  border-left: 4px solid;
  margin-bottom: 12px;
}

.notification-toast:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

/* Toast types */
.toast-info {
  border-left-color: #3989c9;
  background: linear-gradient(135deg, rgba(57, 137, 201, 0.05) 0%, rgba(255, 255, 255, 0.98) 30%);
}

.toast-important {
  border-left-color: #1577da;
  background: linear-gradient(135deg, rgba(21, 119, 218, 0.05) 0%, rgba(255, 255, 255, 0.98) 30%);
}

.toast-warning {
  border-left-color: #f57c02;
  background: linear-gradient(135deg, rgba(245, 124, 2, 0.05) 0%, rgba(255, 255, 255, 0.98) 30%);
}

/* Close button */
.toast-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 2;
}

.toast-close:hover {
  background: rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

/* Icon */
.toast-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(57, 137, 201, 0.9), rgba(0, 179, 190, 0.9));
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.3);
}

.toast-warning .toast-icon {
  background: linear-gradient(135deg, rgba(245, 124, 2, 0.9), rgba(255, 152, 0, 0.9));
  box-shadow: 0 4px 12px rgba(245, 124, 2, 0.3);
}

.toast-important .toast-icon {
  background: linear-gradient(135deg, rgba(21, 119, 218, 0.9), rgba(57, 137, 201, 0.9));
  box-shadow: 0 4px 12px rgba(21, 119, 218, 0.3);
}

/* Content */
.toast-content {
  flex: 1;
  min-width: 0;
  padding-right: 20px;
}

.toast-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toast-message {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toast-time {
  font-size: 11px;
  color: #999;
  font-weight: 500;
}

/* Progress bar */
.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, #3989c9, #00b3be);
  transition: width 0.05s linear;
}

.toast-warning .toast-progress {
  background: linear-gradient(90deg, #f57c02, #ff9800);
}

.toast-important .toast-progress {
  background: linear-gradient(90deg, #1577da, #3989c9);
}

/* Animations */
.slide-down-enter-active {
  animation: slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-leave-active {
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideDown {
  from {
    transform: translateY(-120%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-120%);
    opacity: 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .notification-toast {
    min-width: 320px;
    max-width: calc(100vw - 32px);
  }
}
</style>
